import { Capacitor } from '@capacitor/core'
import { useEffect, useState } from 'react'

import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { BiometricsAppPermission } from '../state/biometrics.atom'
import { useBiometricsPermission } from './useBiometricsPermission'

/**
 * Hook to determine if the current user can use biometrics for authentication
 *
 * Biometrics can only be used if:
 * 1. User is NOT logged in as a mandatee (mandate users must always use eHealth)
 * 2. Biometrics are available on the device
 * 3. User has granted biometric permission
 * 4. Credentials exist in the keychain
 * 5. Keychain credentials match the current user's accountId
 */
export const useCanUseBiometrics = () => {
  const [canUseBiometrics, setCanUseBiometrics] = useState<boolean | null>(null)

  // Hooks
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const { biometricPermission } = useBiometricsPermission()

  useEffect(() => {
    const checkCanUseBiometrics = async () => {
      if (!Capacitor.isNativePlatform()) {
        setCanUseBiometrics(false)
        return
      }

      try {
        // 1. Mandate users can NEVER use biometrics
        if (isLoggedInForMandatee) {
          setCanUseBiometrics(false)
          return
        }

        // 2. Check if biometrics permission is granted
        if (biometricPermission !== BiometricsAppPermission.GRANTED) {
          setCanUseBiometrics(false)
          return
        }

        setCanUseBiometrics(true)
      } catch (error) {
        console.error('Error checking biometric availability:', error)
        setCanUseBiometrics(false)
      }
    }

    checkCanUseBiometrics()
  }, [isLoggedInForMandatee, biometricPermission])

  return {
    canUseBiometrics,
  }
}
